import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface WaveformPlayerProps {
  waveformData: number[];
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  isPlaying?: boolean;
  height?: number;
}

const WaveformPlayer: React.FC<WaveformPlayerProps> = ({
  waveformData,
  currentTime,
  duration,
  onSeek,
  isPlaying = false,
  height = 200,
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const spectrumRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);

  const progress = duration > 0 ? (currentTime / duration) : 0;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!waveformRef.current) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickProgress = clickX / rect.width;
    const seekTime = clickProgress * duration;

    onSeek(seekTime);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!waveformRef.current) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    setHoverPosition(hoverX / rect.width);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Use the waveform data or create fallback
  const samples = waveformData && waveformData.length > 0
    ? waveformData
    : Array.from({ length: 100 }, () => Math.random() * 100);

  // Create mirrored waveform for visual effect (top and bottom)
  const createWaveformPath = () => {
    const width = 1000; // SVG viewBox width
    const height = 100; // SVG viewBox height
    const step = width / samples.length;

    // Create top wave
    const topPath = samples.map((amplitude, i) => {
      const x = i * step;
      const y = (height / 2) - (amplitude / 2);
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    }).join(' ');

    // Create bottom wave (mirrored)
    const bottomPath = samples.map((amplitude, i) => {
      const x = (samples.length - 1 - i) * step;
      const y = (height / 2) + (amplitude / 2);
      return `L ${x},${y}`;
    }).join(' ');

    return `${topPath} ${bottomPath} Z`;
  };

  const waveformPath = createWaveformPath();

  // Frequency spectrum bars (using waveform data as approximation)
  const spectrumBars = samples.slice(0, 64); // Use 64 bars for spectrum

  return (
    <div className="w-full space-y-3">
      {/* Time-domain waveform with integrated progress */}
      <div
        ref={waveformRef}
        className="relative w-full bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden cursor-pointer group"
        style={{ height: '100px' }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
      >
        {/* SVG Waveform */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 1000 100"
          preserveAspectRatio="none"
        >
          {/* Unplayed waveform */}
          <path
            d={waveformPath}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-gray-300 dark:text-gray-700"
            vectorEffect="non-scaling-stroke"
          />

          {/* Played waveform (clipped by progress) */}
          <defs>
            <clipPath id="progress-clip">
              <rect x="0" y="0" width={progress * 1000} height="100" />
            </clipPath>
          </defs>
          <path
            d={waveformPath}
            fill="none"
            stroke="url(#waveform-gradient)"
            strokeWidth="2"
            clipPath="url(#progress-clip)"
            vectorEffect="non-scaling-stroke"
          />
          <linearGradient id="waveform-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#9333EA" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#C084FC" />
          </linearGradient>

          {/* Fill under played waveform */}
          <path
            d={waveformPath}
            fill="url(#waveform-fill)"
            clipPath="url(#progress-clip)"
            opacity="0.2"
          />
          <linearGradient id="waveform-fill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#9333EA" />
            <stop offset="100%" stopColor="#9333EA" stopOpacity="0" />
          </linearGradient>
        </svg>

        {/* Progress line with handle */}
        <motion.div
          className="absolute inset-y-0 w-0.5 bg-purple-500 shadow-lg z-10"
          style={{ left: `${progress * 100}%` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2">
            <motion.div
              className="w-3 h-3 rounded-full bg-purple-500 shadow-lg"
              whileHover={{ scale: 1.5 }}
              animate={isPlaying ? {
                boxShadow: [
                  '0 0 0 0 rgba(168, 85, 247, 0.4)',
                  '0 0 0 10px rgba(168, 85, 247, 0)',
                ],
              } : {}}
              transition={{
                duration: 1.5,
                repeat: isPlaying ? Infinity : 0,
              }}
            />
          </div>
        </motion.div>

        {/* Hover indicator */}
        {isHovering && (
          <motion.div
            className="absolute inset-y-0 w-px bg-white/50 z-5 pointer-events-none"
            style={{ left: `${hoverPosition * 100}%` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              {formatTime(hoverPosition * duration)}
            </div>
          </motion.div>
        )}

        {/* Time indicators overlaid on waveform */}
        <div className="absolute bottom-2 left-2 text-xs font-medium text-gray-700 dark:text-gray-300 tabular-nums">
          {formatTime(currentTime)}
        </div>
        <div className="absolute bottom-2 right-2 text-xs font-medium text-gray-500 dark:text-gray-500 tabular-nums">
          {formatTime(duration)}
        </div>
      </div>

      {/* Frequency spectrum bars */}
      <div
        ref={spectrumRef}
        className="relative w-full bg-gradient-to-br from-purple-900 via-pink-900 to-purple-900 dark:from-purple-950 dark:via-pink-950 dark:to-purple-950 rounded-lg overflow-hidden"
        style={{ height: `${height - 120}px` }}
      >
        {/* Spectrum bars */}
        <div className="absolute inset-0 flex items-end justify-around px-2 py-2">
          {spectrumBars.map((amplitude, index) => {
            const barProgress = index / spectrumBars.length;
            const isInPlayedRegion = barProgress <= progress;

            return (
              <motion.div
                key={index}
                className={`flex-1 mx-px rounded-t transition-all duration-150 ${
                  isInPlayedRegion
                    ? 'bg-gradient-to-t from-purple-500 via-pink-400 to-purple-300'
                    : 'bg-gradient-to-t from-purple-700/50 via-pink-600/50 to-purple-500/50'
                }`}
                style={{
                  height: `${Math.max(5, amplitude)}%`,
                  opacity: isInPlayedRegion ? 1 : 0.4
                }}
                animate={isPlaying && isInPlayedRegion ? {
                  height: [`${Math.max(5, amplitude)}%`, `${Math.max(5, amplitude * 1.2)}%`, `${Math.max(5, amplitude)}%`],
                  opacity: [1, 0.8, 1],
                } : {}}
                transition={{
                  duration: 0.3 + Math.random() * 0.4,
                  repeat: isPlaying && isInPlayedRegion ? Infinity : 0,
                  ease: 'easeInOut',
                }}
              />
            );
          })}
        </div>

        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />
      </div>
    </div>
  );
};

export default WaveformPlayer;
